import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import type { AppWithStats, SettingsResponse, StreakSettingRow } from '@/core/types';
import { api } from '@/lib/api';

type Props = {
  settings: SettingsResponse;
  apps: AppWithStats[];
};

export function StreakSection({ settings, apps }: Props) {
  const queryClient = useQueryClient();

  // Build initial state from settings.streak, defaulting to (true, 1) for any installed
  // active app that doesn't yet have a row.
  const activeApps = apps.filter((a) => a.status === 'active');
  const initial: StreakSettingRow[] = activeApps.map((a) => {
    const existing = settings.streak.find((s) => s.app_id === a.id);
    return existing ?? { app_id: a.id, included_in_overall: true, sessions_required_per_day: 1 };
  });

  const [perApp, setPerApp] = useState<StreakSettingRow[]>(initial);
  const [freezesPerMonth, setFreezesPerMonth] = useState(settings.user_streak.freezes_per_month);
  const [autoSpend, setAutoSpend] = useState(settings.user_streak.freezes_auto_spend === 1);

  const mutation = useMutation({
    mutationFn: api.saveStreakSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      queryClient.invalidateQueries({ queryKey: ['streak', 'overall'] });
      queryClient.invalidateQueries({ queryKey: ['apps'] });
      toast.success('Streak settings saved');
    },
    onError: (err: Error) => toast.error('Failed to save', { description: err.message }),
  });

  const updateApp = (appId: string, patch: Partial<StreakSettingRow>) =>
    setPerApp((rows) => rows.map((r) => (r.app_id === appId ? { ...r, ...patch } : r)));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate({
          freezes_per_month: freezesPerMonth,
          freezes_auto_spend: autoSpend,
          apps: perApp,
        });
      }}
      className="flex flex-col gap-6"
    >
      <section className="flex flex-col gap-3">
        <div>
          <Label className="text-base">Apps included in the overall streak</Label>
          <p className="text-muted-foreground text-xs">
            Toggle which apps must be practiced for the streak to count today.
          </p>
        </div>
        {perApp.map((row) => {
          const app = activeApps.find((a) => a.id === row.app_id);
          return (
            <div
              key={row.app_id}
              className="border-border bg-card flex flex-wrap items-center justify-between gap-3 rounded-md border p-3"
            >
              <div className="flex items-center gap-3">
                <Switch
                  checked={row.included_in_overall}
                  onCheckedChange={(v) => updateApp(row.app_id, { included_in_overall: v })}
                />
                <span className="font-medium">{app?.display_name ?? row.app_id}</span>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-muted-foreground text-xs">sessions/day</Label>
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={row.sessions_required_per_day}
                  onChange={(e) =>
                    updateApp(row.app_id, {
                      sessions_required_per_day: Math.max(1, Number(e.target.value) || 1),
                    })
                  }
                  className="h-9 w-20 text-center"
                />
              </div>
            </div>
          );
        })}
      </section>

      <section className="flex flex-col gap-3">
        <div className="flex items-baseline justify-between">
          <Label>Freezes per month</Label>
          <Input
            type="number"
            min={0}
            max={10}
            value={freezesPerMonth}
            onChange={(e) => setFreezesPerMonth(Math.max(0, Number(e.target.value) || 0))}
            className="h-9 w-20 text-center"
          />
        </div>
        <div className="border-border bg-card flex items-center justify-between rounded-md border p-3">
          <div>
            <Label>Auto-spend freezes</Label>
            <p className="text-muted-foreground text-xs">
              Missed days automatically spend a freeze instead of breaking your streak.
            </p>
          </div>
          <Switch checked={autoSpend} onCheckedChange={setAutoSpend} />
        </div>
      </section>

      <div>
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  );
}
